import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForceChangePassword() {
    const { user, logout } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/auth/change-password`, {
                current_password: currentPassword,
                new_password: newPassword,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Password changed! Please log in again.');
            logout();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-8" data-testid="force-change-password">
            <div className="bg-white rounded-2xl border shadow-lg p-8 max-w-md w-full">
                <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <KeyRound className="w-8 h-8 text-amber-600" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">Password Change Required</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Your password was reset by an administrator. Please set a new password to continue.
                    </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="current-pw">Current / Temporary Password</Label>
                        <Input
                            id="current-pw"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            className="mt-1"
                            data-testid="current-password-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="new-pw">New Password (min 6 chars)</Label>
                        <Input
                            id="new-pw"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            className="mt-1"
                            data-testid="new-password-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="confirm-pw">Confirm New Password</Label>
                        <Input
                            id="confirm-pw"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            className="mt-1"
                            data-testid="confirm-password-input"
                        />
                    </div>
                    <Button type="submit" className="w-full bg-[#0F2A44]" disabled={loading} data-testid="change-password-btn">
                        <Lock className="w-4 h-4 mr-2" />
                        {loading ? 'Changing...' : 'Change Password & Continue'}
                    </Button>
                </form>
            </div>
        </div>
    );
}
